import "./master-module.css";

import { Knob } from "./knob.ts";
import { Meter } from "./meter.ts";
import { ToggleSwitch } from "./toggle-switch.ts";

// MasterModule — final stage of the FX column. Sits below the FxRack
// (not in it — master isn't an "FX", it's the output amp).
//
//   • `gain` knob → `master_gain` (Smoothed 0..1.5, default 0.7)
//   • LED-style output meter driven by the engine's sample tap; the
//     consumer kicks off a raf loop that polls `sampleTap()` and feeds
//     the peak to `meter.setLevel()`.
//
// The meter's internal peak-hold + decay logic smooths the level
// reading; we just hand it the rectified peak of the latest sample
// window each frame.

/** Number of samples polled per frame to compute the meter peak.
 *  ~5 ms of audio at 48 kHz = 240 samples; 256 gives a small margin and
 *  picks up brief transients without smearing across frames. */
const METER_WINDOW_SAMPLES = 256;

export interface MasterState {
  gain: number;
  /** True = mono / legato (one voice). False = poly (8 voices). Default
   *  mono so existing patches sound identical at boot. Optional so older
   *  serialised presets that predate the toggle load without breaking. */
  mono?: boolean;
}

export interface MasterModuleOptions {
  container: HTMLElement;
  /** Engine bridge. Master needs continuous `master_gain` plus the
   *  discrete `voice.mode` for the mono/poly toggle. */
  setParam: (path: string, value: number) => void;
  setDiscrete: (path: string, value: number) => void;
  /** Returns the most recent N mono samples from the engine ring
   *  buffer. Typically `(n) => synth.sampleTap(n)`. */
  sampleTap: (n: number) => Float32Array;
  initial?: Partial<MasterState>;
}

export class MasterModule {
  private root: HTMLDivElement;
  private meter: Meter;
  private gainKnob!: Knob;
  private monoToggle!: ToggleSwitch;
  private sampleTap: (n: number) => Float32Array;
  private rafHandle = 0;
  private running = false;

  constructor(opts: MasterModuleOptions) {
    const init = opts.initial ?? {};
    const gain = init.gain ?? 0.7;
    const mono = init.mono ?? true;
    this.sampleTap = opts.sampleTap;

    this.root = document.createElement("div");
    this.root.className = "master-module";

    const header = document.createElement("div");
    header.className = "master-module-header";
    const title = document.createElement("span");
    title.className = "master-module-title";
    title.textContent = "master";
    header.append(title);

    // Mono / poly toggle. Active = mono (legato, single voice) per
    // plan.md's original aesthetic. Off = poly (8-voice allocator).
    const toggleHost = document.createElement("div");
    toggleHost.className = "master-module-voice-mode";
    header.append(toggleHost);
    this.monoToggle = new ToggleSwitch({
      container: toggleHost,
      label: "mono",
      labelPosition: "left",
      active: mono,
      onToggle: (active) => {
        // voice.mode: 0 = mono, 1 = poly. The engine releases sounding
        // voices on flip so we don't strand them.
        opts.setDiscrete("voice.mode", active ? 0 : 1);
      },
    });

    this.root.append(header);

    const body = document.createElement("div");
    body.className = "master-module-body";
    this.root.append(body);

    // Knob on the left.
    this.gainKnob = new Knob({
      container: body,
      size: 44,
      min: 0,
      max: 1.5,
      default: 0.7,
      value: gain,
      label: "gain",
      format: (v) => `${Math.round(v * 100)}%`,
      onChange: (v) => opts.setParam("master_gain", v),
    });

    // Meter occupies the right side of the body row.
    const meterHost = document.createElement("div");
    meterHost.className = "master-module-meter";
    body.append(meterHost);
    this.meter = new Meter({
      container: meterHost,
      width: 200,
      style: "led",
      ledCount: 16,
      clipThreshold: 0.99,
    });

    opts.container.append(this.root);

    this.start();
  }

  /** Begin polling the sample tap each frame and feeding the meter.
   *  Called automatically on construct; pause/resume isn't currently
   *  needed but the methods are here so the meter can be parked when
   *  audio is paused, if we want that later. */
  start(): void {
    if (this.running) return;
    this.running = true;
    const tick = (): void => {
      if (!this.running) return;
      // Pull the most recent sample window from the engine's ring
      // buffer and feed the rectified peak to the meter. The meter
      // owns its own peak-hold / decay smoothing, so handing it the
      // raw window peak is fine.
      const samples = this.sampleTap(METER_WINDOW_SAMPLES);
      let peak = 0;
      for (let i = 0; i < samples.length; i++) {
        const a = samples[i] !== undefined ? Math.abs(samples[i]!) : 0;
        if (a > peak) peak = a;
      }
      this.meter.setLevel(peak);
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafHandle);
  }

  getState(): MasterState {
    return { gain: this.gainKnob.current, mono: this.monoToggle.isActive() };
  }

  setState(s: MasterState): void {
    this.gainKnob.setValue(s.gain);
    if (typeof s.mono === "boolean") {
      this.monoToggle.setActive(s.mono);
    }
  }

  destroy(): void {
    this.stop();
    this.meter.destroy();
    this.root.remove();
  }
}
